/**
 * CentralisedPTPlans — top-level page for the isolated Centralised PT Plan
 * feature. Mirrors the visual structure of the existing /centralized-plan
 * "Addon" page (toolbar + CustomTable + modal-body-k modals + icon actions)
 * so HQ users get a consistent UX across centralised-plan tools.
 *
 * Route: /centralized-pt-plan-beta
 */

import React, { Suspense, lazy, useCallback, useEffect, useState } from "react";
import Modal from "react-modal";
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import HelmetWrapper from "../../HelmetWrapper";
import { selectToken, selectUser } from "../../components/auth/auth.selector";
import Loader from "../../components/ui/Loader";
import CustomTable from "../../components/components-bootstrap/CustomTable";
import PaginationModified from "../../data/PaginationModified";
import { useGetFetch } from "../../data/Queries";
import {
  addonCategories,
  addonCategoryEndPoint,
} from "../../data/ApiEndpoints";
import { fetchPtPlanDraftsData } from "../../redux/plans-reducers/ptPlansSlice";
import { deletePtPlanDraft } from "../../redux/plans-reducers/ptPlansApis";
import { formatDate } from "../../utils/utilityfunc";

const AddPTPlanFormPolished = lazy(() =>
  import("../../components/centralised-pt-plan/AddPTPlanFormPolished")
);
// Reuse the proven Addons "Map Addon" wizard for PT plans. PT plan drafts are
// addon_master_draft rows (same model as addons), so this writes to the same
// addon_master/session_master/plan_price tables that PT's mapped-gyms view reads.
const AddAddon = lazy(() =>
  import("../../components/centralised-plan-system/AddAddon")
);
const MappedGymsView = lazy(() =>
  import("../../components/centralised-pt-plan/MappedGymsView")
);

const CloseX = ({ onClick }) => (
  <button
    className="btn-k small btn-muted-k icon-close-k rounded-full-k"
    onClick={onClick}
    aria-label="Close">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      fill="currentColor"
      viewBox="0 0 16 16">
      <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708" />
    </svg>
  </button>
);

const PencilIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    fill="currentColor"
    viewBox="0 0 16 16">
    <path d="M12.854.146a.5.5 0 0 0-.707 0L10.5 1.793 14.207 5.5l1.647-1.646a.5.5 0 0 0 0-.708zm.646 6.061L9.793 2.5 3.293 9H3.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.207zm-7.468 7.468A.5.5 0 0 1 6 13.5V13h-.5a.5.5 0 0 1-.5-.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.5-.5V10h-.5a.5.5 0 0 1-.175-.032l-.179.178a.5.5 0 0 0-.11.168l-2 5a.5.5 0 0 0 .65.65l5-2a.5.5 0 0 0 .168-.11z" />
  </svg>
);
const TrashIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    fill="currentColor"
    viewBox="0 0 16 16">
    <path d="M11 1.5v1h3.5a.5.5 0 0 1 0 1h-.538l-.853 10.66A2 2 0 0 1 11.115 16h-6.23a2 2 0 0 1-1.994-1.84L2.038 3.5H1.5a.5.5 0 0 1 0-1H5v-1A1.5 1.5 0 0 1 6.5 0h3A1.5 1.5 0 0 1 11 1.5m-5 0v1h4v-1a.5.5 0 0 0-.5-.5h-3a.5.5 0 0 0-.5.5M4.5 5.029l.5 8.5a.5.5 0 1 0 .998-.06l-.5-8.5a.5.5 0 1 0-.998.06m6.53-.528a.5.5 0 0 0-.528.47l-.5 8.5a.5.5 0 0 0 .998.058l.5-8.5a.5.5 0 0 0-.47-.528M8 4.5a.5.5 0 0 0-.5.5v8.5a.5.5 0 0 0 1 0V5a.5.5 0 0 0-.5-.5" />
  </svg>
);
const PlusIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    fill="currentColor"
    viewBox="0 0 16 16">
    <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4" />
  </svg>
);
const EyeIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    fill="currentColor"
    viewBox="0 0 16 16">
    <path d="M10.5 8a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0" />
    <path d="M0 8s3-5.5 8-5.5S16 8 16 8s-3 5.5-8 5.5S0 8 0 8m8 3.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7" />
  </svg>
);

const CentralisedPTPlans = () => {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const token = useSelector(selectToken);
  const { ptDrafts, ptDraftsLoading } = useSelector((state) => state.ptPlans);

  // Modal state — separate booleans to mirror the existing Addon page's style
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isMapOpen, setIsMapOpen] = useState(false);
  const [isMappedOpen, setIsMappedOpen] = useState(false);
  const [currentRow, setCurrentRow] = useState(null);

  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ limit: 10, search: "" });
  const [isPage, setIsPage] = useState(0);

  // Categories prop for the form
  const { data: addonCategoriesData } = useGetFetch({
    url: addonCategoryEndPoint,
    filters: { status: "active" },
    token,
    key: addonCategories,
  });

  const loadDrafts = useCallback(() => {
    if (!user?.token) return;
    dispatch(
      fetchPtPlanDraftsData({
        filters: {
          page: isPage + 1,
          limit: filters.limit,
          search: filters.search,
          status: "active",
        },
        token: user.token,
      })
    );
  }, [dispatch, user?.token, isPage, filters]);

  useEffect(() => {
    loadDrafts();
  }, [loadDrafts]);

  const onApplySearch = () => {
    if (search !== filters.search) {
      setFilters((f) => ({ ...f, search }));
      setIsPage(0);
    }
  };
  const onReset = () => {
    setSearch("");
    setFilters((f) => ({ ...f, search: "" }));
    setIsPage(0);
  };

  const onAdd = () => {
    setCurrentRow(null);
    setIsAddOpen(true);
  };
  const onEdit = (row) => {
    setCurrentRow(row);
    setIsEditOpen(true);
  };
  const onMap = (row) => {
    setCurrentRow(row);
    setIsMapOpen(true);
  };
  const onViewMapped = (row) => {
    setCurrentRow(row);
    setIsMappedOpen(true);
  };
  const onDelete = async (row) => {
    if (!window.confirm("Delete this PT plan? Mapped gyms will be deactivated."))
      return;
    const res = await deletePtPlanDraft(row.uid, token);
    if (res?.status) {
      toast.success(res.message || "Deleted");
      loadDrafts();
    } else {
      toast.error(res?.message || "Failed to delete");
    }
  };

  const closeAll = () => {
    setIsAddOpen(false);
    setIsEditOpen(false);
    setIsMapOpen(false);
    setIsMappedOpen(false);
    setCurrentRow(null);
  };

  const COLS = [
    {
      dataField: "date_added",
      text: "Date Added",
      formatter: (_, row) => (
        <span className="flex-k py-2 fs-normal-k flex-center">
          {row?.date_added
            ? formatDate(Number(row.date_added) * 1000) || "N/A"
            : "N/A"}
        </span>
      ),
    },
    {
      dataField: "addon_name",
      text: "Plan Name",
      formatter: (_, row) => (
        <span className="flex-k py-2 fs-normal-k flex-center">
          {row?.addon_name || "N/A"}
        </span>
      ),
    },
    {
      dataField: "category_name",
      text: "Category",
      formatter: (_, row) => (
        <span className="flex-k py-2 fs-normal-k flex-center">
          {row?.category_name || "PT"}
        </span>
      ),
    },
    {
      dataField: "gym_category_name",
      text: "For Gym Tier",
      formatter: (_, row) => (
        <div className="flex-k flex-center">
          {row?.gym_category_name ? (
            <span className="badge bg-info text-capitalize fs-6">
              {row.gym_category_name}
            </span>
          ) : (
            <span
              className="badge bg-secondary text-capitalize fs-6"
              title="Not pinned — any gym can be mapped">
              Any
            </span>
          )}
        </div>
      ),
    },
    {
      dataField: "addon_for",
      text: "For",
      formatter: (_, row) => (
        <span className="flex-k py-2 fs-normal-k flex-center">
          {row?.addon_for || "N/A"}
        </span>
      ),
    },
    {
      dataField: "intensity",
      text: "Intensity",
      formatter: (_, row) => (
        <span className="flex-k py-2 fs-normal-k flex-center">
          {row?.intensity || "N/A"}
        </span>
      ),
    },
    {
      dataField: "session_details",
      text: "Sessions",
      formatter: (_, row) => (
        <span className="flex-k py-2 fs-normal-k flex-center">
          {row?.session_details?.length || 0}
        </span>
      ),
    },
    {
      dataField: "mapped_data",
      text: "Mapped Gyms",
      formatter: (_, row) => {
        const count = Array.isArray(row?.mapped_data)
          ? row.mapped_data.filter(Boolean).length
          : 0;
        return (
          <div className="flex-k flex-center">
            <span
              className="table-link-k"
              onClick={() => count > 0 && onViewMapped(row)}>
              {count || "N/A"}
            </span>
          </div>
        );
      },
    },
    {
      dataField: "status",
      text: "Status",
      formatter: (_, row) => {
        const isActive = row?.status === "active";
        return (
          <div className="flex-k flex-center">
            <span
              className={`badge text-capitalize fs-6 ${
                isActive ? "bg-success" : "bg-danger"
              }`}>
              {row?.status || "N/A"}
            </span>
          </div>
        );
      },
    },
    {
      dataField: "action",
      text: "Action",
      formatter: (_, row) => (
        <div className="flex-k gap-05-k flex-center">
          <button
            onClick={() => onEdit(row)}
            className="btn-k btn-muted-k dark-text small"
            title="Edit">
            <PencilIcon />
          </button>
          <button
            onClick={() => onDelete(row)}
            className="btn-k btn-muted-k dark-text small"
            title="Delete">
            <TrashIcon />
          </button>
          <button
            onClick={() => onMap(row)}
            className="btn-k btn-muted-k dark-text small"
            title="Map to gyms">
            <PlusIcon />
          </button>
          <button
            onClick={() => onViewMapped(row)}
            className="btn-k btn-muted-k dark-text small"
            title="View mapped gyms">
            <EyeIcon />
          </button>
        </div>
      ),
    },
  ];

  const rows = ptDrafts?.data || [];
  const pagination = ptDrafts?.pagination || [];

  return (
    <HelmetWrapper title="WTF: Centralised PT Plans">
      <div className="px-50px py-3" style={{ background: "#f7f8fa", minHeight: "100vh" }}>
        <h1 style={{ fontWeight: 700, marginBottom: 18 }}>Centralised PT Plans</h1>

        {/* Top toolbar — Add | Search/Reset/Rows (matches /centralized-plan layout) */}
        <div className="flex-k js-between-k">
          <div className="grid-k grid-col-2-k w-full-k items-end-k js-betweem-k gap-3 p-1">
            <div className="flex-k items-end-k">
              <button className="btn-k btn-primary-k" onClick={onAdd}>
                + Add Centralised PT Plan
              </button>
            </div>
            <div className="grid-k grid-col-2-k js-end-k items-end-k gap-05-k">
              <div className="flex-k flex-col-k">
                <label className="dark-text">Search</label>
                <input
                  className="input-k grid-span-2-k"
                  type="text"
                  placeholder="Search By Plan Name.."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && onApplySearch()}
                />
              </div>
              <div className="grid-k w-full-k grid-col-3-k items-end-k gap-2">
                <button className="btn-k w-full-k dark-bg-k" onClick={onApplySearch}>
                  Search
                </button>
                <button className="btn-k w-full-k dark-bg-k" onClick={onReset}>
                  Reset
                </button>
                <div className="flex-k flex-col-k items-end-k">
                  <div className="flex-k flex-col-k">
                    <label className="text-muted fs-smaller-k">Rows:</label>
                    <Select
                      options={[5, 10, 20, 50].map((n) => ({ label: n, value: n }))}
                      value={{ label: filters.limit, value: filters.limit }}
                      onChange={(opt) =>
                        setFilters((f) => ({ ...f, limit: opt?.value || 10 }))
                      }
                      placeholder="Rows"
                      classNamePrefix={"input-k-select small"}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        {ptDraftsLoading ? (
          <div className="my-5 py-5">
            <Loader />
          </div>
        ) : (
          <div className="grid-k gap-3">
            <CustomTable data={rows} columns={COLS} />
            <div className="flex-k js-end-k">
              <PaginationModified
                isPage={isPage}
                onChangePage={setIsPage}
                pagination={pagination}
              />
            </div>
          </div>
        )}

        {/* Add Modal */}
        <Modal ariaHideApp={false} isOpen={isAddOpen}>
          <div className="modal-body-k bg-white rounded-5">
            <div className="flex-k js-between-k">
              <h1>Add Centralised PT Plan</h1>
              <CloseX onClick={closeAll} />
            </div>
            <Suspense fallback={<Loader />}>
              <AddPTPlanFormPolished
                onClose={closeAll}
                refetch={loadDrafts}
                addonCategoriesData={addonCategoriesData}
              />
            </Suspense>
          </div>
        </Modal>

        {/* Edit Modal */}
        <Modal ariaHideApp={false} isOpen={isEditOpen}>
          <div className="modal-body-k bg-white rounded-5">
            <div className="flex-k js-between-k">
              <h1>Edit Centralised PT Plan</h1>
              <CloseX onClick={closeAll} />
            </div>
            <Suspense fallback={<Loader />}>
              <AddPTPlanFormPolished
                onClose={closeAll}
                refetch={loadDrafts}
                intitailData={currentRow}
                addonCategoriesData={addonCategoriesData}
              />
            </Suspense>
          </div>
        </Modal>

        {/* Map Modal */}
        <Modal ariaHideApp={false} isOpen={isMapOpen}>
          <div className="modal-body-k bg-white rounded-5">
            <div className="flex-k js-between-k">
              <h1>Map to Gyms</h1>
              <CloseX onClick={closeAll} />
            </div>
            <Suspense fallback={<Loader />}>
              <AddAddon
                isMapAddon
                intitailData={currentRow}
                addonCategoriesData={addonCategoriesData}
                refetch={loadDrafts}
                onClose={closeAll}
                mapCreateUrl="/addon/web/pt"
                mapUpdateUrl="/addon/web/pt/update"
                gymCategoryId={currentRow?.gym_category_id}
                restrictToDraftUid={currentRow?.uid}
              />
            </Suspense>
          </div>
        </Modal>

        {/* View Mapped Gyms Modal */}
        <Modal ariaHideApp={false} isOpen={isMappedOpen}>
          <div className="modal-body-k small rounded-5 bg-white">
            <div className="flex-k js-between-k">
              <h1>Mapped Gyms</h1>
              <CloseX onClick={closeAll} />
            </div>
            <Suspense fallback={<Loader />}>
              <MappedGymsView
                rowData={currentRow}
                closeModal={closeAll}
                callFunction={loadDrafts}
              />
            </Suspense>
          </div>
        </Modal>
      </div>
    </HelmetWrapper>
  );
};

export default CentralisedPTPlans;
